import { ServiceClassBadge } from '../../components/ServiceClassBadge';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, formatQuantity, ORDER_FILE_LIMIT, ORDER_STATUS_LABELS, ORDER_WORK_STATUSES, orderEditable, orderOverdue, type Order, type OrderFile, type OrderLogEntry, type OrderStatus, type OrderTask, type Paginated, type Task } from '@kontor/shared';
import { api, ApiError, apiUpload, queryString } from '../../api';
import { useSession } from '../../session';
import { AssignmentsPanel } from '../fleet/AssignmentsPanel';
import { CardLayout, FactBoxPart, FactRow } from '../../components/CardLayout';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { LinesView } from '../offers/LinesEditor';
import { OrderInvoices } from '../finance';
import { orderTone, serviceRange } from './OrdersPage';
import { OrderFlow } from './OrderFlow';
import { OrderDispatch } from './OrderDispatch';
import { PrepaymentBanner } from './PrepaymentBanner';
import { RideLogPanel } from './RideLogPanel';
import { RideReportsPanel } from './RideReportsPanel';
import { FollowUpPanel } from '../crm';
import './orders.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const day = (d: string) => dateFormat.format(new Date(`${d}T12:00:00`));
const size = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`);
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/**
 * Ansicht eines Auftrags (KON-125 bis KON-130): Kopf mit Nummer und Stand, Kunde, Herkunft und Leistungszeitraum, Positionen mit Summen, Aufgaben, Dateien und
 * unten der Verlauf. Was erlaubt ist, entscheidet der Server, die Seite blendet nur aus, was nicht passt.
 */
export function OrderDetailPage() {
  const { id = '' } = useParams();
  const { can, hasModule } = useSession();
  const order = useQuery({ queryKey: ['order', id], queryFn: async () => (await api<{ data: Order }>('GET', `/orders/${id}`)).data });

  if (order.isLoading) return <p className="muted">Wird geladen …</p>;
  if (order.isError || !order.data) {
    return (
      <div className="card narrow">
        <h1>Auftrag nicht gefunden</h1>
        <Link to="/auftraege">Zur Liste</Link>
      </div>
    );
  }
  const o = order.data;
  const write = can('orders.write');
  const c = o.customer;

  return (
    <>
      <p><Link to="/auftraege" className="back-link">← Alle Aufträge</Link></p>
      <div className="page-head">
        <h1>{o.number}</h1>
        <span className={orderTone(o.status)}>{ORDER_STATUS_LABELS[o.status]}</span>
        <span className="muted">{o.subject}</span>
        <ServiceClassBadge value={o.serviceClass} />
        {write && orderEditable(o.status) && <Link className="btn btn--secondary btn--sm page-head__action" to={`/auftraege/${o.id}/bearbeiten`}>Bearbeiten</Link>}
      </div>
      {o.status === 'cancelled' && <div className="alert alert--error">Storniert am {stamp.format(new Date(o.cancelledAt!))} Uhr{o.cancelReason ? `: „${o.cancelReason}“` : ''}</div>}
      {o.status === 'invoiced' && <div className="alert alert--ok">Abgerechnet{o.invoicedAt ? ` am ${stamp.format(new Date(o.invoicedAt))} Uhr` : ''}. Der Auftrag lässt sich nicht mehr ändern.</div>}

      {orderOverdue(o) && <div className="alert alert--warn" role="status"><strong>Stand und Leistungsdatum passen nicht zusammen.</strong> {orderOverdue(o)!.text}</div>}

      <PrepaymentBanner order={o} />

      <CardLayout factbox={<OrderFactBox order={o} />}>
      <OrderFlow order={o} />
      <OrderDispatch order={o} />
      <RideLogPanel order={o} />
      <RideReportsPanel order={o} />
      {write && o.status !== 'invoiced' && <StatusPanel order={o} />}

      <div className="card">
        <div className="offer-head">
          <div>
            <div className="muted small">Kunde</div>
            <Link to={`/kontakte/${o.contactId}`} className="strong-link">{c.name}</Link>
            {c.company && c.company !== c.name && <div>{c.company}</div>}
            <div>{c.street}</div>
            <div>{[c.postalCode, c.city].filter(Boolean).join(' ')}</div>
            {c.email && <div className="muted small">{c.email}</div>}
          </div>
          <dl className="offer-meta">
            <dt>Angelegt</dt>
            <dd>{dateFormat.format(new Date(o.createdAt))}{o.createdByName ? ` von ${o.createdByName}` : ''}</dd>
            <dt>Leistungs Datum</dt>
            <dd>{serviceRange(o.serviceStart, o.serviceEnd)}</dd>
            {o.offerId && (<><dt>Angebot</dt><dd><Link to={`/angebote/${o.offerId}`}>{o.offerNumber ?? 'Angebot'}</Link></dd></>)}
            {o.inquiryId && (<><dt>Anfrage</dt><dd><Link to={`/anfragen/${o.inquiryId}`}>{o.inquiryReference}</Link></dd></>)}
            <dt>Bestellnr.</dt>
            <dd>{o.customerReference ?? o.number}</dd>
            {o.completedAt && (<><dt>Erledigt</dt><dd>{dateFormat.format(new Date(o.completedAt))}</dd></>)}
          </dl>
        </div>
        {o.pricesHidden ? <PositionsOnly order={o} /> : <LinesView lines={o.lines} totals={o.totals} discountBp={o.discountBp} smallBusiness={o.smallBusiness} />}
        {o.notes && (
          <>
            <h3>Interne Notiz</h3>
            <p className="order-notes">{o.notes}</p>
          </>
        )}
      </div>

      <OrderInvoices orderId={o.id} status={o.status} />
      {hasModule('calendar') && <OrderTasks order={o} />}
      {hasModule('fleet') && can('fleet.read') && <AssignmentsPanel orderId={o.id} defaultStart={o.serviceStart} defaultEnd={o.serviceEnd} locked={o.status === 'done' || o.status === 'invoiced'} />}
      <FollowUpPanel subjectKind="order" subjectId={o.id} />
      <OrderFiles order={o} />
      {write && <OrderLog order={o} />}
      </CardLayout>
    </>
  );
}

/** Positionen ohne Preise, für alle, die den Auftrag nur ansehen dürfen. Die Zahlen schickt der Server gar nicht erst. */
function PositionsOnly({ order }: { order: Order }) {
  return (
    <table className="table table--cards offer-table">
      <thead>
        <tr>
          <th>Pos.</th>
          <th>Bezeichnung</th>
          <th className="num">Menge</th>
        </tr>
      </thead>
      <tbody>
        {order.lines.map((l) => (
          <tr key={l.id}>
            <td data-label="Pos.">{l.position}</td>
            <td>
              <strong>{l.title}</strong>
              {l.description && <div className="muted small offer-desc">{l.description}</div>}
            </td>
            <td data-label="Menge" className="num">{formatQuantity(l.quantityMilli)} {l.unit}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Stand wechseln (KON-127). Die vier Arbeitsstände stehen als Schritte da, Stornieren verlangt einen Grund. */
function StatusPanel({ order }: { order: Order }) {
  const client = useQueryClient();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const change = useMutation({
    mutationFn: async (body: { status: OrderStatus; reason?: string }) => (await api<{ data: Order }>('POST', `/orders/${order.id}/status`, body)).data,
    onSuccess: (o) => {
      client.setQueryData(['order', o.id], o);
      client.invalidateQueries({ queryKey: ['orders'] });
      setCancelling(false);
      setReason('');
      setError(null);
    },
    onError: (e) => setError(errorText(e, 'Der Stand konnte nicht geändert werden.')),
  });

  if (order.status === 'cancelled') {
    return (
      <div className="card offer-actions">
        <button type="button" className="btn btn--secondary btn--sm" disabled={change.isPending} onClick={() => window.confirm('Auftrag wieder öffnen? Er steht danach auf „Offen“.') && change.mutate({ status: 'open' })}>Wieder öffnen</button>
        {error && <div className="alert alert--error" role="alert">{error}</div>}
      </div>
    );
  }
  return (
    <section className="card" aria-label="Stand">
      <h2 className="sr-only">Stand</h2>
      <ul className="order-steps">
        {ORDER_WORK_STATUSES.map((s) => (
          <li key={s}>
            <button type="button" className="btn btn--secondary btn--sm" aria-pressed={order.status === s} disabled={change.isPending} onClick={() => order.status !== s && change.mutate({ status: s })}>
              {ORDER_STATUS_LABELS[s]}
            </button>
          </li>
        ))}
        <li>
          <button type="button" className="btn btn--danger btn--sm" disabled={change.isPending} onClick={() => setCancelling((v) => !v)}>Stornieren …</button>
        </li>
      </ul>
      {cancelling && (
        <form
          className="order-cancel"
          onSubmit={(e) => {
            e.preventDefault();
            if (!reason.trim()) return setError('Bitte geben Sie einen Grund für die Stornierung an.');
            change.mutate({ status: 'cancelled', reason: reason.trim() });
          }}
        >
          <label className="field">
            <span>Grund der Stornierung</span>
            <textarea className="input textarea textarea--short" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
          </label>
          <div className="form-actions">
            <button type="submit" className="btn btn--danger btn--sm" disabled={change.isPending}>Auftrag stornieren</button>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setCancelling(false)}>Abbrechen</button>
          </div>
        </form>
      )}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
    </section>
  );
}

/** Verknüpfte Aufgaben (KON-128): neue anlegen, vorhandene suchen und verknüpfen, Verknüpfung lösen */
function OrderTasks({ order }: { order: Order }) {
  const { can } = useSession();
  const client = useQueryClient();
  const write = can('orders.write') && order.status !== 'invoiced';
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['order', order.id, 'tasks'], queryFn: async () => (await api<{ data: OrderTask[] }>('GET', `/orders/${order.id}/tasks`)).data });
  const search = useQuery({
    queryKey: ['tasks', 'pick', query],
    queryFn: async () => (await api<Paginated<Task>>('GET', `/tasks${queryString({ q: query, group: 'open', perPage: 8 })}`)).data,
    enabled: write && can('calendar.read') && query.trim().length >= 2,
  });
  const refresh = () => {
    client.invalidateQueries({ queryKey: ['order', order.id, 'tasks'] });
    client.invalidateQueries({ queryKey: ['order', order.id, 'log'] });
  };
  const link = useMutation({
    mutationFn: (taskId: string) => api('POST', `/orders/${order.id}/tasks`, { taskId }),
    onSuccess: () => {
      setQuery('');
      setError(null);
      refresh();
    },
    onError: (e) => setError(errorText(e, 'Die Aufgabe konnte nicht verknüpft werden.')),
  });
  const unlink = useMutation({ mutationFn: (taskId: string) => api('DELETE', `/orders/${order.id}/tasks/${taskId}`), onSuccess: refresh, onError: (e) => setError(errorText(e, 'Das hat nicht geklappt.')) });
  const linkedIds = new Set((list.data ?? []).map((t) => t.id));

  return (
    <CollapsibleCard
      title="Zugehörige Aufgaben"
      storageKey="order-tasks"
      defaultOpen
      actions={write && can('calendar.write') ? <Link className="btn btn--secondary btn--sm" to={`/kalender/aufgaben/neu?auftrag=${order.id}`}>Neue Aufgabe</Link> : null}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {list.isLoading && <p className="muted">Wird geladen …</p>}
      {list.data?.length === 0 && <p className="muted">{can('orders.write') || can('calendar.write') ? 'Noch keine Aufgabe verknüpft.' : 'Keine Aufgabe, bei der Sie zuständig oder beteiligt sind.'}</p>}
      {list.data && list.data.length > 0 && (
        <ul className="order-list">
          {list.data.map((t) => (
            <li key={t.id}>
              <span className="order-list__main">
                {can('calendar.read') ? <Link to={`/kalender/aufgaben/${t.id}`} className="strong-link">{t.title}</Link> : <strong>{t.title}</strong>}
                <span className="muted small">
                  {t.dueDate ? ` · fällig ${day(t.dueDate)}` : t.startDate ? ` · ${serviceRange(t.startDate, t.endDate)}` : ''}
                  {t.assignedUserName ? ` · ${t.assignedUserName}` : ''}
                </span>
              </span>
              <span className={`order-status${t.status === 'done' ? ' order-status--done' : ''}`}>{t.status === 'done' ? 'Erledigt' : 'Offen'}</span>
              {write && <button type="button" className="btn btn--secondary btn--sm" disabled={unlink.isPending} onClick={() => window.confirm(`Verknüpfung mit „${t.title}“ lösen? Die Aufgabe bleibt bestehen.`) && unlink.mutate(t.id)}>Lösen</button>}
            </li>
          ))}
        </ul>
      )}
      {write && can('calendar.read') && (
        <div className="order-link">
          <input className="input" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Vorhandene offene Aufgabe suchen …" aria-label="Vorhandene Aufgabe suchen" />
        </div>
      )}
      {search.data && query.trim().length >= 2 && (
        <ul className="order-list">
          {search.data.filter((t) => !linkedIds.has(t.id)).map((t) => (
            <li key={t.id}>
              <span className="order-list__main">{t.title}{t.dueDate ? <span className="muted small"> · fällig {day(t.dueDate)}</span> : null}</span>
              <button type="button" className="btn btn--primary btn--sm" disabled={link.isPending} onClick={() => link.mutate(t.id)}>Verknüpfen</button>
            </li>
          ))}
          {search.data.filter((t) => !linkedIds.has(t.id)).length === 0 && <li className="muted">Keine passende offene Aufgabe.</li>}
        </ul>
      )}
    </CollapsibleCard>
  );
}

/** Dateien zum Auftrag (KON-129) */
function OrderFiles({ order }: { order: Order }) {
  const { can } = useSession();
  const client = useQueryClient();
  const write = can('orders.write') && order.status !== 'invoiced';
  const [error, setError] = useState<string | null>(null);
  const files = useQuery({ queryKey: ['order', order.id, 'files'], queryFn: async () => (await api<{ data: OrderFile[] }>('GET', `/orders/${order.id}/files`)).data });
  const refresh = () => {
    client.invalidateQueries({ queryKey: ['order', order.id, 'files'] });
    client.invalidateQueries({ queryKey: ['order', order.id, 'log'] });
  };
  const upload = useMutation({
    mutationFn: (file: File) => apiUpload<{ data: OrderFile }>(`/orders/${order.id}/files`, file),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e) => setError(errorText(e, 'Die Datei konnte nicht hochgeladen werden.')),
  });
  const remove = useMutation({ mutationFn: (fileId: string) => api('DELETE', `/orders/${order.id}/files/${fileId}`), onSuccess: refresh, onError: (e) => setError(errorText(e, 'Die Datei konnte nicht entfernt werden.')) });
  const full = (files.data?.length ?? 0) >= ORDER_FILE_LIMIT;

  return (
    <CollapsibleCard
      title="Auftrags Dateien"
      storageKey="order-files"
      defaultOpen
      actions={
        write ? (
          <label className={`btn btn--secondary btn--sm${upload.isPending || full ? ' is-disabled' : ''}`}>
            {upload.isPending ? 'Wird hochgeladen …' : 'Datei hinzufügen'}
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
              className="sr-only"
              disabled={upload.isPending || full}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) upload.mutate(file);
              }}
            />
          </label>
        ) : null
      }
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {files.isLoading && <p className="muted">Wird geladen …</p>}
      {files.data?.length === 0 && <p className="muted">Noch keine Dateien. Zum Beispiel der unterschriebene Vertrag, ein Aufmaß oder Fotos vom Einsatzort.</p>}
      {files.data && files.data.length > 0 && (
        <ul className="order-list">
          {files.data.map((f) => (
            <li key={f.id}>
              <span className="order-list__main">
                <a href={`/api/v1/orders/${order.id}/files/${f.id}/download`} className="strong-link">{f.fileName}</a>
                <span className="muted small"> · {size(f.sizeBytes)} · {dateFormat.format(new Date(f.createdAt))}{f.uploadedByName ? ` · ${f.uploadedByName}` : ''}</span>
              </span>
              {write && <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => window.confirm(`„${f.fileName}“ entfernen?`) && remove.mutate(f.id)}>Entfernen</button>}
            </li>
          ))}
        </ul>
      )}
      <p className="muted small">PDF, JPG, PNG, WEBP oder HEIC, bis 5 MB je Datei, höchstens {ORDER_FILE_LIMIT} Dateien.</p>
    </CollapsibleCard>
  );
}

const FIELD_LABELS: Record<string, string> = {
  contactId: 'Kunde', subject: 'Betreff', customerReference: 'Bestellnummer', serviceStart: 'Leistung von', serviceEnd: 'Leistung bis', notes: 'Interne Notiz', discountBp: 'Rabatt', lines: 'Positionen', grossCents: 'Betrag',
};
const status = (s: unknown) => ORDER_STATUS_LABELS[s as OrderStatus] ?? String(s);
const fromTo = (v: unknown) => v as { from: number; to: number };

const LOG_LABELS: Record<string, (d: Record<string, unknown>) => string> = {
  ride_report: (d) => `Nachmeldung des Fahrers: ${d.kind === 'extra_km' ? `${d.quantity} zusätzliche Kilometer` : d.kind === 'waiting' ? `${d.quantity} Minuten zusätzliche Wartezeit` : 'Notiz'}${d.note ? `, „${String(d.note)}“` : ''}`,
  ride_report_handled: () => 'Nachmeldung des Fahrers als erledigt markiert',
  ride_report_reopened: () => 'Nachmeldung des Fahrers wieder geöffnet',
  driver_release: (d) => `Vom Fahrer zurückgegeben${d.employee ? ` (${String(d.employee)})` : ''}${d.reason ? `: „${String(d.reason)}“` : ''}`,
  create: (d) => `Angelegt als ${d.number ?? 'Auftrag'}${typeof d.grossCents === 'number' ? ` über ${formatCents(d.grossCents)}` : ''}`,
  create_from_offer: (d) => `Aus dem angenommenen Angebot ${d.offerNumber ?? ''} entstanden`.replace('  ', ' '),
  update: (d) => {
    const fields = Object.keys(d).filter((k) => k !== 'grossCents').map((k) => FIELD_LABELS[k] ?? k);
    const gross = d.grossCents ? ` (Betrag ${formatCents(fromTo(d.grossCents).from)} → ${formatCents(fromTo(d.grossCents).to)})` : '';
    return `Geändert: ${fields.join(', ') || 'Betrag'}${gross}`;
  },
  status: (d) => `Stand ${status(d.from)} → ${status(d.to)}${d.reason ? `: „${d.reason}“` : ''}`,
  handover: (d) => `An das Personalmanagement übergeben${d.note ? `: „${d.note}“` : ''}`,
  handover_withdraw: () => 'Übergabe zurückgenommen',
  handover_return: (d) => `Vom Personalmanagement zurückgegeben: „${d.reason ?? ''}“`,
  planning_done: () => 'Planung abgeschlossen',
  task_link: (d) => `Aufgabe „${d.title ?? ''}“ verknüpft`,
  task_unlink: (d) => `Verknüpfung mit Aufgabe „${d.title ?? ''}“ gelöst`,
  file_add: (d) => `Datei „${d.fileName ?? ''}“ hinzugefügt`,
  file_remove: (d) => `Datei „${d.fileName ?? ''}“ entfernt`,
};

/** Verlauf unten im Auftrag (KON-130), aus dem Protokoll */
function OrderLog({ order }: { order: Order }) {
  const log = useQuery({ queryKey: ['order', order.id, 'log', order.updatedAt], queryFn: async () => (await api<{ data: OrderLogEntry[] }>('GET', `/orders/${order.id}/log`)).data });
  return (
    <CollapsibleCard title="Verlauf" storageKey="order-log">
      {log.isLoading && <p className="muted">Wird geladen …</p>}
      {log.isError && <p className="muted">Der Verlauf konnte nicht geladen werden.</p>}
      {log.data && log.data.length > 0 && (
        <ul className="history-list">
          {log.data.map((e) => (
            <li key={e.id}>
              <span className="history-list__when">{stamp.format(new Date(e.at))} Uhr</span>
              <span>
                {(LOG_LABELS[e.action] ?? (() => e.action))(e.data)}
                <span className="muted">{e.actorName ? ` von ${e.actorName}` : ' vom System'}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </CollapsibleCard>
  );
}

/**
 * Infobox des Auftrags (wie die FactBox in Business Central): Stand, Kunde, Leistungsdatum, Zahl der Aufgaben und Dateien. Preise und Angebot nur für Verwalter,
 * der Server liefert sie den anderen gar nicht erst.
 */
function OrderFactBox({ order: o }: { order: Order }) {
  const { hasModule } = useSession();
  const tasks = useQuery({ queryKey: ['order', o.id, 'tasks'], queryFn: async () => (await api<{ data: OrderTask[] }>('GET', `/orders/${o.id}/tasks`)).data, enabled: hasModule('calendar') });
  const files = useQuery({ queryKey: ['order', o.id, 'files'], queryFn: async () => (await api<{ data: OrderFile[] }>('GET', `/orders/${o.id}/files`)).data });
  const open = (tasks.data ?? []).filter((t) => t.status === 'open').length;

  return (
    <>
      <FactBoxPart title="Übersicht" storageKey="order-overview">
        <FactRow label="Nummer">{o.number}</FactRow>
        <FactRow label="Stand">{ORDER_STATUS_LABELS[o.status]}</FactRow>
        <FactRow label="Kunde"><Link to={`/kontakte/${o.contactId}`}>{o.contactName}</Link></FactRow>
        <FactRow label="Leistungs Datum">{serviceRange(o.serviceStart, o.serviceEnd) === '–' ? null : serviceRange(o.serviceStart, o.serviceEnd)}</FactRow>
        <FactRow label="Bestellnr.">{o.customerReference ?? o.number}</FactRow>
        <FactRow label="Angelegt">{dateFormat.format(new Date(o.createdAt))}</FactRow>
        <FactRow label="Von">{o.createdByName}</FactRow>
      </FactBoxPart>

      {!o.pricesHidden && (
        <FactBoxPart title="Beträge" storageKey="order-amounts">
          <FactRow label="Netto">{formatCents(o.totals.netCents)}</FactRow>
          <FactRow label="Steuer">{formatCents(o.totals.taxCents)}</FactRow>
          <FactRow label="Brutto"><strong>{formatCents(o.totals.grossCents)}</strong></FactRow>
          {o.offerId && <FactRow label="Angebot"><Link to={`/angebote/${o.offerId}`}>{o.offerNumber ?? 'Angebot'}</Link></FactRow>}
        </FactBoxPart>
      )}

      <FactBoxPart title="Positionen" storageKey="order-positions">
        <FactRow label="Anzahl">{String(o.lines.length)}</FactRow>
      </FactBoxPart>

      {hasModule('calendar') && (
        <FactBoxPart title="Aufgaben" storageKey="order-task-count">
          <FactRow label="Offen">{tasks.isLoading ? '…' : String(open)}</FactRow>
          <FactRow label="Erledigt">{tasks.isLoading ? '…' : String((tasks.data ?? []).length - open)}</FactRow>
        </FactBoxPart>
      )}

      <FactBoxPart title="Dateien" storageKey="order-file-count">
        <FactRow label="Anzahl">{files.isLoading ? '…' : String((files.data ?? []).length)}</FactRow>
      </FactBoxPart>
    </>
  );
}
